<template>
  <div class="modal fade home-news-modal" id="homeNewsModal" data-bs-backdrop="static" tabindex="-1" aria-labelledby="homeNewsModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content" v-if="activeItem">
        <div class="modal-header">
          <div class="home-news-brand">
            <h5 class="modal-title">{{ labels.eyebrow }}</h5>
            <IconEyerCloudLogo class="home-news-logo"/>
          </div>

          <button type="button" class="btn btn-outline-secondary rounded-pill btn-back" data-bs-dismiss="modal" aria-label="Close" @click="close">
            {{ labels.close }}
          </button>
        </div>

        <div class="modal-body">
          <nav class="home-news-nav">
            <button v-for="(item, index) in news" :key="item.key || index" type="button"
                    class="home-news-nav-item" :class="{ active: index === activeIndex }"
                    @click="activeIndex = index">
              <span>{{ item.label }}</span>
              <svg v-if="index === activeIndex" width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L6 6L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </nav>

          <div class="home-news-content">
            <img v-if="activeItem.image" class="home-news-preview" :src="activeItem.image" :alt="activeItem.label"/>

            <span class="home-news-tag">{{ labels.tag }}</span>
            <h4 class="home-news-title">{{ activeItem.title }}</h4>
            <p class="home-news-description">{{ activeItem.description }}</p>

            <a v-if="activeItem.link" :href="activeItem.link" target="_blank" rel="noopener" class="btn btn-outline-secondary btn-news-learn-more">
              {{ labels.learnMore }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.home-news-modal {
  .modal-dialog {
    max-width:                          525px;
  }

  .modal-content {
    @include                            border_radius(16px);
    @include                            box_shadow(0 20px 60px rgba(0, 0, 0, 0.25));
    overflow:                           hidden;
    border:                             none;
    background-color:                   var(--Shades-BG);
  }

  .modal-header,
  .modal-body {
  }

  .modal-header {
    align-items:                        flex-start;
    justify-content:                    space-between;
    padding:                            30px;
    border:                             0;
  }

  .home-news-brand {
    display:                            flex;
    flex-direction:                     column;
  }

  .modal-title {
    color:                              var(--Brand-Primary);
    font-size:                          11px;
    font-weight:                        700;
    line-height:                        125%; /* 13.75px */
    letter-spacing:                     1.21px;
    text-transform:                     uppercase;
    margin:                             0;
  }

  .home-news-logo {
    width:                              135px;
    height:                             auto;
  }

  .modal-body {
    display:                            flex;
    gap:                                25px;
    padding:                            0 30px 30px;
  }

  .home-news-nav {
    display:                            flex;
    flex:                               0 0 160px;
    flex-direction:                     column;
    gap:                                18px;
    padding:                            10px 20px 10px 0;
    border-right:                       1px solid var(--Shades-20-Border);
  }

  .home-news-nav-item {
    color:                              var(--Brand-Primary);
    font-size:                          var(--font-size-14);
    font-weight:                        400;
    text-align:                         left;
    display:                            flex;
    align-items:                        center;
    justify-content:                    space-between;
    gap:                                8px;
    padding:                            0;
    background:                         none;
    border:                             none;

    &.active {
      font-weight:                      700;
    }

    &:hover {
      color:                            var(--Brand-Primary);
    }
  }

  .home-news-content {
    flex:                               1 1 auto;
    gap:                                10px;
    // padding:                            28px 32px;
  }

  .home-news-preview {
    width:                              100%;
    max-height:                         220px;
    object-fit:                         cover;
    @include                            border_radius(8px);
  }

  .home-news-tag {
    display:                            block;
    color:                              var(--Brand-Primary);
    font-size:                          var(--font-size-10);
    font-weight:                        700;
    letter-spacing:                     1.89px;
    text-transform:                     uppercase;
    margin:                             10px 0 5px;
  }

  .home-news-title {
    color:                              var(--Brand-Primary);
    font-size:                          var(--font-size-18);
    font-weight:                        600;
    line-height:                        1.3;
  }

  .home-news-description {
    color:                              var(--Shades-60);
    font-size:                          var(--font-size-14);
    line-height:                        1.6;
    margin:                             0;
  }

  .btn-news-learn-more {
    color:                              var(--Brand-Primary);
    font-size:                          var(--font-size-12);
    font-weight:                        700;
    letter-spacing:                     3.6px;
    text-transform:                     uppercase;
    width:                              fit-content;
    margin-top:                         10px;
    padding:                            10px 20px;
    border-color:                       var(--Brand-Primary);
  }
}

@media (max-width: 575px) {
  .home-news-modal {
    .modal-header {
      flex-wrap:                        wrap;
      gap:                              16px;
    }

    .modal-body {
      flex-direction:                   column;
    }

    .home-news-nav {
      flex:                             0 0 auto;
      flex-direction:                   row;
      flex-wrap:                        wrap;
      border-right:                     none;
      border-bottom:                    1px solid var(--Shades-20-Border);
    }
  }
}
</style>

<style lang="scss">
:root.dark-theme {
  .home-news-logo {
    path:nth-child(-n+4) {
      fill:                             white;
    }
  }
}
</style>

<script>
import IconEyerCloudLogo from '@/components/icons/IconEyerCloudLogo.vue'
import UtilService from '@/services/util-service.js'

// hosted by marketing on the Phelcom institutional site, same convention as the banners feed (used by auth-login.vue)
// {
//   "newsVersion": "001.0",
//   "baseUrl": "https://phelcom.tech/pastelzim/content/media/",
//   "news": {
//     "pt-br": [{ "key": "quickReport", "label": "...", "title": "...", "description": "...", "file": "banner-news.png", "link": "https://..." }],
//     "en": [...],
//     "es": [...]
//   }
// }
const NEWS_CONFIG_URL = 'https://phelcom.tech/pastelzim/content/feed/news'

const SEEN_VERSION_KEY = 'home-news-seen-version'

const UI_LABELS = {
  'pt-br': { eyebrow: 'Novidades no', tag: 'Novidade', close: 'Fechar', learnMore: 'Saber mais' },
  en: { eyebrow: "What's new in", tag: 'New', close: 'Close', learnMore: 'Learn more' },
  es: { eyebrow: 'Novedades en', tag: 'Novedad', close: 'Cerrar', learnMore: 'Saber más' },
}

export default {
  name: 'HomeNewsModal',
  components: {
    IconEyerCloudLogo,
  },
  data: () => ({
    news: [],
    newsVersion: null,
    activeIndex: 0,
    home_news_modal: null,
  }),
  computed: {
    locale() {
      return (this.$i18n && this.$i18n.locale) || UtilService.getCurrentLanguage()
    },

    labels() {
      return UI_LABELS[this.locale] || UI_LABELS['pt-br']
    },

    activeItem() {
      return this.news[this.activeIndex] || null
    },
  },
  methods: {
    async fetchNews() {
      try {
        const response = await fetch(`${NEWS_CONFIG_URL}?t=${Date.now()}`)
        const config = await response.json()
        const items = (config.news && (config.news[this.locale] || config.news['en'])) || []

        this.newsVersion = config.newsVersion
        this.news = items.map(item => ({
          ...item,
          image: item.file ? `${config.baseUrl}${item.file}?v=${config.newsVersion}` : null,
        }))
      } catch (err) {
        console.error('[HomeNewsModal] failed to load news config', err)
        this.news = []
      }
    },

    // shown once per newsVersion (tracked in localStorage) unless force=true (menu button)
    // keyed on the feed's own version, not the app version: an eyercloud release with no new
    // content in the feed must not re-show something the user already saw
    hasSeenCurrentVersion() {
      return !!this.newsVersion && localStorage.getItem(SEEN_VERSION_KEY) === this.newsVersion
    },

    markCurrentVersionAsSeen() {
      localStorage.setItem(SEEN_VERSION_KEY, this.newsVersion)
    },

    async show({ force = false } = {}) {
      await this.fetchNews()

      if (!this.news.length)
        return false

      if (!force && this.hasSeenCurrentVersion())
        return false

      this.activeIndex = 0
      this.home_news_modal = new bootstrap.Modal(document.getElementById('homeNewsModal')).show()
      this.markCurrentVersionAsSeen()
      return true
    },

    close() {
      this.$emit('close')
    },
  },
}
</script>
